import { Field, Text } from "@sitecore-jss/sitecore-jss-react";
import React from "react";

import { paymentTerms } from "../../Enums/enumValues";
import { allMandatory } from "../../helpers/allMandatory/allMandatory";
import { useDupFlowContext } from "../../hooks/useDupContext";
import { DupInsuranceProductType, PersonCoverage } from "../../interfaces/dup";
import { Enum } from "../../interfaces/enum";
import { FlowSidebar } from "../../layouts/FlowSidebar";
import useIsClient from "../../lib/useIsClient";
import { isCoveragesComplete } from "../../providers/dupFlow/DupFlow.helpers";
import { Button, Props as ButtonProps } from "../buttons/Button";
import { currencyFormat } from "../InputFields/sanitizer/currencyFormat";
import { VerticalRadioInput } from "../InputFields/VerticalRadioInput";
import { SummaryTable } from "../summary/SummaryTable";
import { DupInsuredSummary } from "./calculation/DupInsuredSummary";
import styles from "./DupSummary.module.scss";
import { DupSummaryData } from "./DupSummaryData";

interface Props {
  cannotEdit?: boolean;
  submitButtonProps: ButtonProps;
  fields: Record<string, Field<string>>;
}

function DupSummary({ cannotEdit = false, submitButtonProps, ...props }: Props) {
  const { premium, isLoadingPremium, modelState, setModelState, insuredAmounts, setters, data } = useDupFlowContext();
  const { isClient } = useIsClient();
  const insureds = data.insuredPersons;

  const hasInsureds = allMandatory(
    insureds,
    insureds.length > 0,
    insureds[0].firstName !== undefined,
    insureds[0].dateOfBirth !== undefined
  );

  const hasMultipleInsuredPersons = insureds.length > 1;

  const hasCoverage = isCoveragesComplete(data.defaultCoverage);

  const terms = premium ? premium.totalPremiumPerTerms : [];

  const premiumTermsOptions = terms.map((term) => {
    const option = (paymentTerms() as Enum[]).filter((x) => x.value === term.paymentTerm);
    return {
      value: term.paymentTerm,
      label: option.length ? option[0].label : "",
    };
  });

  const selectedPremiumValue = terms.filter((t) => t.paymentTerm === data.paymentTerm);
  const selectedPremium =
    data.paymentTerm && selectedPremiumValue.length > 0 ? currencyFormat(selectedPremiumValue[0].premium) : "";

  const showPremium = hasCoverage && isClient && terms.length > 0;

  const showFields = hasInsureds && hasCoverage;

  const insuredPersonTitle = hasMultipleInsuredPersons
    ? props.fields.InsuredPersonsTitle
    : props.fields.InsuredPersonTitle;

  return (
    <>
      <FlowSidebar
        title={
          <>
            <Text field={props.fields.Title} />
            {isLoadingPremium && <div className="loadingspinner" title="Loading" />}
          </>
        }
      >
        {showPremium && (
          <div className={styles.dupSummary__prices}>
            <div>
              <p>
                <strong>{<Text field={props.fields.ChoosePaymentTermLabel} />}</strong>
              </p>
              <VerticalRadioInput
                wrapOptionInDiv="No"
                radioRowClassName="form-item full-width radio"
                selectedRadioItem={data.paymentTerm}
                onChange={setters.updatePaymentTerm}
                disabled={cannotEdit}
                name="PaymentTerm"
                options={premiumTermsOptions}
              />
            </div>
            <div className={styles.dupSummary__price}>{selectedPremium}</div>
          </div>
        )}
        <div className={styles.dupSummary__content}>
          {!hasInsureds && (
            <p>
              <Text field={props.fields.NoDataLabel} />
            </p>
          )}
          {hasInsureds && (
            <SummaryTable>
              <thead>
                <tr>
                  <th colSpan={2}>
                    <Text field={insuredPersonTitle}></Text>
                  </th>
                </tr>
              </thead>
              <tbody>
                {insureds.map((insured, index) => {
                  const updateInsuredCoverage = (value: PersonCoverage, isApplied: boolean) => {
                    setters.updateInsuredCoverage(index, value, isApplied);
                  };
                  const updateInsuredCoverageInsuranceType = (value: DupInsuranceProductType) => {
                    setters.updateInsuredCoverageInsuranceType(index, value);
                  };
                  const updateInsuredCoverageInsuredAmount = (value: number) => {
                    setters.updateInsuredCoverageInsuredAmount(index, value);
                  };
                  return (
                    <DupInsuredSummary
                      key={`InsuredSummary${index}`}
                      index={index}
                      updateInsuredCoverage={updateInsuredCoverage}
                      updateInsuredCoverageInsuranceType={updateInsuredCoverageInsuranceType}
                      updateInsuredCoverageInsuredAmount={updateInsuredCoverageInsuredAmount}
                      modelState={modelState}
                      setModelState={setModelState}
                      fields={props.fields}
                      insured={insured}
                      defaultCoverage={data.defaultCoverage}
                      insuredAmounts={insuredAmounts}
                      hasMultipleInsuredPersons={hasMultipleInsuredPersons}
                    />
                  );
                })}
              </tbody>
            </SummaryTable>
          )}
          {showFields && <DupSummaryData fields={props.fields} />}
        </div>
      </FlowSidebar>
      <div className={styles["dupSummary__submit-button-wrapper"]}>
        <Button
          {...submitButtonProps}
          leading={<i className={`icon icon-pencil ${styles["dupSummary__submit-button-icon"]}`} />}
          type="primary"
        />
      </div>
    </>
  );
}

export default DupSummary;
